import React, { useState, useEffect } from "react";
import { AlertTriangle, Package, Layers, TrendingDown, TrendingUp, Pencil, Check, X, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api from "../utils/api";
import { formatMXN, categoryLabels } from "../utils/format";

export default function Inventario() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [catFilter, setCatFilter] = useState("todos");
  const [tab, setTab] = useState("stock");
  const [projection, setProjection] = useState([]);
  const [lots, setLots] = useState([]);
  const [editingStock, setEditingStock] = useState(null); // product id being edited
  const [newStockValue, setNewStockValue] = useState("");
  const [adjustReason, setAdjustReason] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [invRes, projRes, lotsRes] = await Promise.all([
        api.get("/inventory"),
        api.get("/inventory/projection").catch(() => ({ data: [] })),
        api.get("/inventory-lots/summary").catch(() => ({ data: [] })),
      ]);
      setProducts(invRes.data); setProjection(projRes.data); setLots(lotsRes.data);
    } catch { toast.error("Error al cargar inventario"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const startEdit = (p) => {
    setEditingStock(p.id);
    setNewStockValue(String(p.stock || 0));
    setAdjustReason("");
  };

  const cancelEdit = () => {
    setEditingStock(null);
    setNewStockValue("");
    setAdjustReason("");
  };

  const saveStockAdjust = async (productId) => {
    const val = parseFloat(newStockValue);
    if (isNaN(val) || val < 0) { toast.error("Cantidad invalida"); return; }
    setSaving(true);
    try {
      await api.post(`/products/${productId}/adjust-stock`, {
        new_stock: val,
        reason: adjustReason || "Ajuste manual desde inventario"
      });
      toast.success("Stock ajustado correctamente");
      cancelEdit();
      load();
    } catch { toast.error("Error al ajustar stock"); }
    finally { setSaving(false); }
  };

  const CATEGORIES = ["todos", "producto_final", "materia_prima", "insumo_directo", "insumo_indirecto", "material_empaque"];
  const filtered = products.filter((p) => catFilter === "todos" || p.category === catFilter);
  const lowStock = products.filter((p) => p.stock <= (p.min_stock || 0) && p.min_stock > 0);
  const totalValue = products.reduce((a, p) => a + (p.stock * (p.cost || 0)), 0);

  return (
    <div className="space-y-6" data-testid="inventario-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Inventario</h1>
          <p className="text-slate-500 text-sm mt-1">Control de existencias — clic en el lapiz para ajustar stock</p>
        </div>
        <button onClick={load} data-testid="refresh-inventory-btn"
          className="flex items-center gap-2 border border-slate-200 text-slate-600 px-3 py-2 rounded-lg text-sm hover:bg-slate-50 transition-colors">
          <RefreshCw size={14} /> Actualizar
        </button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Valor total en inventario</div>
          <div className="text-2xl font-bold text-slate-900">{formatMXN(totalValue)}</div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Total de productos</div>
          <div className="text-2xl font-bold text-slate-900">{products.length}</div>
        </div>
        <div className={`rounded-xl border p-4 ${lowStock.length > 0 ? "bg-amber-50 border-amber-200" : "bg-white border-slate-200"}`}>
          <div className="flex items-center gap-2 mb-1">
            {lowStock.length > 0 && <AlertTriangle size={14} className="text-amber-600" />}
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Con stock bajo</div>
          </div>
          <div className={`text-2xl font-bold ${lowStock.length > 0 ? "text-amber-800" : "text-slate-900"}`}>
            {lowStock.length}
          </div>
        </div>
      </div>

      {/* Low stock alert */}
      {lowStock.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle size={16} className="text-amber-600" />
            <span className="font-semibold text-amber-800 text-sm">Productos con stock bajo o agotado</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {lowStock.map((p) => (
              <div key={p.id} className="bg-white border border-amber-200 rounded-lg px-3 py-1.5 text-xs">
                <span className="font-medium text-slate-800">{p.name}</span>
                <span className="text-amber-700 ml-2">{p.stock} {p.unit} / min. {p.min_stock}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 rounded-lg p-1 w-fit" data-testid="inv-tabs">
        {[{ key: "stock", label: "Stock Actual" }, { key: "proyeccion", label: "Proyeccion" }, { key: "lotes", label: "Por Lote" }].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`inv-tab-${t.key}`}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              tab === t.key ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
            }`}>{t.label}</button>
        ))}
      </div>

      {tab === "stock" && (<>
      {/* Filter */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 flex gap-2 flex-wrap">
        {CATEGORIES.map((c) => (
          <button key={c} onClick={() => setCatFilter(c)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              catFilter === c ? "bg-brand-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}>{c === "todos" ? "Todos" : categoryLabels[c]}</button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <table className="w-full text-sm" data-testid="inventory-table">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              {["Producto", "Categoria", "Existencia", "Unidad", "Stock min.", "Costo", "Valor", "Estado", ""].map((h) => (
                <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} className="text-center py-12 text-slate-400">Cargando...</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={9} className="text-center py-12 text-slate-400">Sin productos</td></tr>
            ) : filtered.map((p) => {
              const isLow = p.stock <= (p.min_stock || 0) && p.min_stock > 0;
              const isOut = p.stock <= 0;
              const isEditing = editingStock === p.id;
              return (
                <tr key={p.id} className={`border-b border-slate-100 transition-colors ${isOut ? "bg-red-50/50" : isLow ? "bg-amber-50/50" : "hover:bg-slate-50"}`}
                  data-testid={`inv-row-${p.id}`}>
                  <td className="px-4 py-3 font-medium text-slate-900">{p.name}</td>
                  <td className="px-4 py-3">
                    <span className="bg-slate-100 text-slate-600 text-xs px-2 py-0.5 rounded-full">{categoryLabels[p.category] || p.category}</span>
                  </td>
                  <td className="px-4 py-3">
                    {isEditing ? (
                      <div className="flex flex-col gap-1">
                        <input type="number" value={newStockValue} onChange={(e) => setNewStockValue(e.target.value)}
                          className="w-24 border border-brand-300 rounded px-2 py-1 text-sm font-semibold focus:outline-none focus:ring-1 focus:ring-brand-500"
                          step="0.01" autoFocus data-testid={`stock-input-${p.id}`} />
                        <input type="text" value={adjustReason} onChange={(e) => setAdjustReason(e.target.value)}
                          placeholder="Motivo (opcional)" data-testid={`stock-reason-${p.id}`}
                          className="w-40 border border-slate-200 rounded px-2 py-0.5 text-[10px] focus:outline-none focus:ring-1 focus:ring-brand-500" />
                      </div>
                    ) : (
                      <span className="font-semibold">{p.stock || 0}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-600">{p.unit}</td>
                  <td className="px-4 py-3 text-slate-500">{p.min_stock || 0}</td>
                  <td className="px-4 py-3 text-slate-700">{formatMXN(p.cost || 0)}</td>
                  <td className="px-4 py-3 font-medium text-slate-900">{formatMXN((p.stock || 0) * (p.cost || 0))}</td>
                  <td className="px-4 py-3">
                    {isOut ? (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-medium">Agotado</span>
                    ) : isLow ? (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">Stock bajo</span>
                    ) : (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-brand-100 text-brand-700 font-medium">Normal</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {isEditing ? (
                      <div className="flex gap-1">
                        <button onClick={() => saveStockAdjust(p.id)} disabled={saving} data-testid={`stock-save-${p.id}`}
                          className="p-1.5 rounded-md bg-emerald-50 hover:bg-emerald-100 transition-colors">
                          <Check size={14} className="text-emerald-600" />
                        </button>
                        <button onClick={cancelEdit} data-testid={`stock-cancel-${p.id}`}
                          className="p-1.5 rounded-md bg-red-50 hover:bg-red-100 transition-colors">
                          <X size={14} className="text-red-500" />
                        </button>
                      </div>
                    ) : (
                      <button onClick={() => startEdit(p)} data-testid={`stock-edit-${p.id}`}
                        className="p-1.5 rounded-md hover:bg-slate-100 transition-colors" title="Ajustar stock">
                        <Pencil size={14} className="text-slate-400 hover:text-slate-600" />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      </>)}

      {/* Projection Tab */}
      {tab === "proyeccion" && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto" data-testid="projection-table">
          <div className="px-4 py-3 border-b border-slate-200">
            <span className="text-sm font-bold text-slate-700">Proyeccion de Inventario</span>
            <span className="text-xs text-slate-400 ml-2">Stock actual + en produccion - pedidos pendientes</span>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                {["Producto", "Stock Actual", "En Produccion", "Demanda Pendiente", "Reservado", "Sin Asignar", "Proyeccion", ""].map(h => (
                  <th key={h} className="text-left px-3 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {projection.map(p => (
                <tr key={p.product_id} className={`border-b border-slate-100 ${p.needs_production ? "bg-red-50/50" : "hover:bg-slate-50"}`}>
                  <td className="px-3 py-2.5 font-medium text-slate-900">{p.product_name}</td>
                  <td className="px-3 py-2.5">{p.current_stock} {p.unit}</td>
                  <td className="px-3 py-2.5 text-blue-600">{p.in_production > 0 ? `+${p.in_production}` : "-"}</td>
                  <td className="px-3 py-2.5 text-amber-600">{p.pending_demand > 0 ? `-${p.pending_demand}` : "-"}</td>
                  <td className="px-3 py-2.5 text-teal-600">{p.allocated > 0 ? p.allocated : "-"}</td>
                  <td className="px-3 py-2.5">{p.unallocated_demand > 0 ? <span className="text-red-600 font-medium">{p.unallocated_demand}</span> : "-"}</td>
                  <td className="px-3 py-2.5">
                    <span className={`font-bold ${p.projected_available >= 0 ? "text-emerald-600" : "text-red-600"}`}>{p.projected_available} {p.unit}</span>
                  </td>
                  <td className="px-3 py-2.5">
                    {p.needs_production && <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-medium">Producir</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Lots Tab */}
      {tab === "lotes" && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto" data-testid="lots-table">
          <div className="px-4 py-3 border-b border-slate-200">
            <span className="text-sm font-bold text-slate-700">Inventario por Lote</span>
            <span className="text-xs text-slate-400 ml-2">Disponible vs reservado por producto</span>
          </div>
          {lots.length === 0 ? (
            <div className="p-12 text-center text-slate-400 text-sm">
              <Layers size={32} className="mx-auto text-slate-300 mb-2" />
              <p>Sin lotes registrados. Registra transformaciones para generar lotes de inventario.</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  {["Producto", "Unidad", "Total", "Disponible", "Reservado", "Lotes"].map(h => (
                    <th key={h} className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {lots.map(l => (
                  <tr key={l.product_id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-2.5 font-medium text-slate-900">{l.product_name}</td>
                    <td className="px-4 py-2.5 text-slate-600">{l.unit}</td>
                    <td className="px-4 py-2.5 font-medium">{l.total_available.toFixed(1)}</td>
                    <td className="px-4 py-2.5 font-bold text-emerald-600">{l.total_free.toFixed(1)}</td>
                    <td className="px-4 py-2.5 font-medium text-amber-600">{l.total_reserved > 0 ? l.total_reserved.toFixed(1) : "-"}</td>
                    <td className="px-4 py-2.5 text-brand-600">{l.lot_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
